import { Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery } from "@mui/material";
import styles from "./containerList.module.css";

/**
 * Skeleton loader for the Container List page.
 * Mirrors the real layout (search + add bar, sticky-header table on
 * desktop, stacked cards on mobile) so there's no layout shift once the
 * data arrives.
 *
 * Usage in containerList.jsx:
 *   import ContainerListLoader from "./ContainerListLoader";
 *   ...
 *   {contentLoading && <ContainerListLoader />}
 */
const ContainerListLoader = ({ rows = 6 }) => {
    const isMobile = useMediaQuery("(max-width:768px)");
    const rowArray = Array.from({ length: rows });

    return (
        <>
            {/* Search + Add button placeholder */}
            <div className={styles.FilterBar}>
                <Skeleton variant="rounded" height={42} width="100%" sx={{ maxWidth: 420, borderRadius: "10px" }} />
                <Skeleton variant="rounded" height={42} width={140} sx={{ borderRadius: "10px" }} />
            </div>

            {isMobile ? (
                <div className={styles.CardList}>
                    {rowArray.map((_, i) => (
                        <ContainerCardSkeleton key={`ccard-${i}`} />
                    ))}
                </div>
            ) : (
                <ContainerTableSkeleton rows={rows} />
            )}
        </>
    );
};

const ContainerTableSkeleton = ({ rows }) => {
    const columns = [
        "Reference Id",
        "Container Number",
        "Freight Forwarder",
        "Container Address",
        "Contact Person Number",
        "Active",
        "",
    ];

    return (
        <div className={styles.TableCard}>
            <TableContainer component={Paper}>
                <Table aria-label="Loading container table">
                    <TableHead>
                        <TableRow>
                            {columns.map((col, i) => (
                                <TableCell key={i}>
                                    <Skeleton variant="text" width={col ? (col.length > 12 ? 100 : 70) : 60} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.from({ length: rows }).map((_, rowIndex) => (
                            <TableRow key={`row-${rowIndex}`}>
                                <TableCell>
                                    <Skeleton variant="text" width="70%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="70%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="60%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="85%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="text" width="65%" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton variant="rounded" width={34} height={20} />
                                </TableCell>
                                <TableCell align="right">
                                    <Skeleton variant="circular" width={28} height={28} sx={{ display: "inline-block", marginRight: 1 }} />
                                    <Skeleton variant="circular" width={28} height={28} sx={{ display: "inline-block" }} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

const ContainerCardSkeleton = () => (
    <div className={styles.CardItem}>
        <div className={styles.CardTopRow}>
            <div style={{ width: "100%" }}>
                <Skeleton variant="text" width="55%" height={22} />
                <Skeleton variant="text" width="35%" height={16} />
            </div>
            <Skeleton variant="rounded" width={34} height={20} />
        </div>

        <div className={styles.CardFieldRow}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="35%" />
        </div>
        <div className={styles.CardFieldRow}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="text" width="45%" />
        </div>
        <div className={styles.CardFieldRow}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="30%" />
        </div>

        <div className={styles.CardActionsRow}>
            <Skeleton variant="circular" width={28} height={28} sx={{ marginRight: 1 }} />
            <Skeleton variant="circular" width={28} height={28} />
        </div>
    </div>
);

export default ContainerListLoader;